
import { useEffect, useRef } from "react";

export const useMetaPixel = (pixelId: string) => {
  // Référence pour savoir si le pixel a déjà été initialisé
  const pixelInitializedRef = useRef(false);
  const initialPageViewSentRef = useRef(false);

  useEffect(() => {
    // Log the pixel ID we're trying to use
    console.log('useMetaPixel hook called with pixelId:', pixelId);
    
    // Validate pixel ID - should typically be a numeric string
    const isValidPixelId = pixelId && pixelId.trim() !== '' && /^\d+$/.test(pixelId.trim());
    
    if (isValidPixelId && typeof window !== 'undefined' && !pixelInitializedRef.current) {
      console.log('Initializing Meta Pixel with valid ID:', pixelId);
      pixelInitializedRef.current = true;
      initializeMetaPixel(pixelId);
      
      // Set a timer to check if PageView was sent after initialization
      setTimeout(() => {
        if (!initialPageViewSentRef.current && typeof window.fbq === 'function') {
          console.log('Sending delayed initial PageView to ensure tracking');
          try {
            window.fbq('track', 'PageView', {
              content_name: document.title,
              content_category: 'page',
              content_type: 'product',
              event_source_url: window.location.href
            });
            initialPageViewSentRef.current = true;
            console.log('Delayed initial PageView sent successfully');
          } catch (error) {
            console.error('Error sending delayed PageView:', error);
          }
        }
      }, 2000); // Wait 2 seconds after initialization
    } else {
      if (!pixelId || pixelId.trim() === '') {
        // Ne pas afficher de message d'erreur si l'ID est vide, juste un log
        console.log('Meta Pixel: No pixel ID provided or pixel disabled');
      } else if (pixelId && !/^\d+$/.test(pixelId.trim())) {
        console.warn('Invalid Meta Pixel ID format (should be numeric):', pixelId);
      }
    }
    
    // Cleanup function to prevent memory leaks
    return () => {
      if (typeof window !== 'undefined' && window.fbq) {
        // No official way to "uninitialize" fbq, but we can remove our own event handlers
        console.log('Cleaning up Meta Pixel');
      }
    };
  }, [pixelId]);

  const initializeMetaPixel = (pixelId: string) => {
    if (!pixelId || typeof window === 'undefined') {
      console.warn('Cannot initialize Meta Pixel - invalid ID or no window object');
      return;
    }
    
    // Check if pixel is already initialized with this ID - safely access getState
    if (window.fbq && typeof window.fbq.getState === 'function') {
      try {
        const state = window.fbq.getState();
        if (state && state.pixelId === pixelId) {
          console.log('Meta Pixel already initialized with ID:', pixelId);
          return;
        }
      } catch (e) {
        console.warn('Error checking Meta Pixel state:', e);
      }
    }

    console.log('Starting Meta Pixel initialization with ID:', pixelId);

    // Standard Facebook Pixel initialization code
    (function(f,b,e,v,n,t,s) {
      if(f.fbq) return;
      n = f.fbq = function() {
        n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments)
      };
      if(!f._fbq) f._fbq = n;
      n.push = n; n.loaded = !0; n.version = '2.0';
      n.queue = []; t = b.createElement(e); t.async = !0;
      t.src = v; s = b.getElementsByTagName(e)[0];
      s && s.parentNode && s.parentNode.insertBefore(t, s);
    })(window as any, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');

    // Initialize with advanced options
    try {
      window.fbq('init', pixelId);
      
      // Allow duplicate page views for safety (can be removed later)
      if (window.fbq.allowDuplicatePageViews !== undefined) {
        window.fbq.allowDuplicatePageViews = true;
      }
      
      // Trigger a PageView immediately after initialization with enhanced data
      window.fbq('track', 'PageView', {
        content_name: document.title,
        content_category: 'page',
        content_type: 'product',
        event_source_url: window.location.href
      });
      initialPageViewSentRef.current = true;
      console.log('Initial PageView sent immediately after pixel initialization');
    } catch (error) {
      console.error('Error during Meta Pixel initialization:', error);
    }
    
    console.log('Meta Pixel initialization complete with ID:', pixelId);
  };

  const sendEvent = (eventName: string, eventParams?: object) => {
    // Additional validation for pixel ID before sending events
    const isValidPixelId = pixelId && pixelId.trim() !== '' && /^\d+$/.test(pixelId.trim());
    
    if (typeof window !== 'undefined' && window.fbq && isValidPixelId) {
      console.log(`Sending ${eventName} event to Meta Pixel with ID ${pixelId}`, eventParams);
      
      try {
        // For standard events use 'track', for custom events use 'trackCustom'
        if (['PageView', 'ViewContent', 'AddToCart', 'InitiateCheckout', 'Purchase', 'Lead', 'CompleteRegistration'].includes(eventName)) {
          // Add event_source_url to all standard events
          const enhancedParams = {
            ...eventParams,
            event_source_url: window.location.href
          };
          
          window.fbq('track', eventName, enhancedParams);
        } else {
          window.fbq('trackCustom', eventName, eventParams);
        }
        console.log(`Sent ${eventName} event to Meta Pixel`, eventParams);
        
        // For PageView events, update our reference
        if (eventName === 'PageView') {
          initialPageViewSentRef.current = true;
        }
      } catch (error) {
        console.error(`Error sending ${eventName} to Meta Pixel:`, error);
      }
    } else {
      // Ne pas afficher d'erreur si l'ID est vide, juste un log
      if (!isValidPixelId) {
        console.log(`Meta Pixel: Cannot send ${eventName} event - No valid pixel ID`);
      } else {
        console.warn(`Cannot send ${eventName} event - Meta Pixel not available`);
      }
    }
  };

  return { 
    sendEvent,
    isInitialized: pixelInitializedRef.current,
    isPageViewSent: initialPageViewSentRef.current
  };
};
